import API from '../config/api';
import { buildPatientHistoryParams } from '../utils/patientClinicalIdentity';
import { cacheOwner, profileGet, rememberPatients } from './profileCache';
import { refreshProfileRevision } from './profileActions';
import { downloadMedia } from './OfflineMedia';
import { auth } from '../firebaseConfig';

export async function preparePatients(doctorId: string, clinicId: string, clinicScopeIds: string[], progress: (message: string) => void) {
  const owner = cacheOwner();
  if (!owner || !navigator.onLine) throw new Error('Connect and sign in to download patient files.');
  const scope = JSON.stringify([clinicId, clinicScopeIds]);
  const scopeParams = clinicId === '__all_clinics__'
    ? { clinic_ids: clinicScopeIds.join(','), use_clinic_scope: 'true' }
    : { clinic_id: clinicId };
  let count = 0;
  let failed = 0;
  let page = 1;
  const seen = new Set<string>();
  while (true) {
    if (cacheOwner() !== owner || !navigator.onLine) throw new Error('Download interrupted. Already downloaded files remain available.');
    const response = await profileGet(`${API.BASE_URL}/api/patients`, {
      params: { doctor_id: doctorId, ...scopeParams, page, limit: 100, sortBy: 'date', sortOrder: 'desc', bypassCache: 'true' },
    });
    if ('offline' in response && response.offline) throw new Error('Download interrupted. Reconnect to continue.');
    const patients = response.data?.data || response.data?.patients || [];
    if (!Array.isArray(patients)) throw new Error('Unexpected patient-list response.');
    const unseen = patients.filter((patient: any) => patient._id && !seen.has(patient._id));
    if (patients.length && !unseen.length) throw new Error('Pagination did not advance. Download is incomplete.');
    await rememberPatients(patients, scope);
    for (const patient of unseen) {
      seen.add(patient._id);
      if (cacheOwner() !== owner || !navigator.onLine) throw new Error('Download interrupted. Already downloaded files remain available.');
      const id = patient.patient_id;
      const params = { doctor_id: patient.doctor_id || doctorId };
      try {
        const completePatient = await refreshProfileRevision(patient);
        if (completePatient) {
          Object.assign(patient, completePatient);
          await rememberPatients([patient], scope);
        }
      } catch { failed++; }
      // Use the same exact identity hints as PatientDetail. Do not reuse one
      // family member's response for another member sharing a phone number.
      const requests: [string, any][] = [
        ['/api/patients/visits', buildPatientHistoryParams(patient, clinicScopeIds)],
        [API.ENDPOINTS.PATIENT_VITALS(id), { ...params, clinic_id: clinicId || patient.clinic_id || '' }],
        [API.ENDPOINTS.MEDICAL_HISTORY_PATIENT, { ...params, patient_id: id }],
        [API.ENDPOINTS.MEDICAL_HISTORY_TEMPLATE, params],
        [API.ENDPOINTS.MEDICAL_REPORTS(patient._id), {}],
        [API.ENDPOINTS.REFERRALS(patient._id), {}],
        [API.ENDPOINTS.PATIENT_ACTIVE_PACKAGES(id), clinicId ? { clinic_id: clinicId } : params],
        [API.ENDPOINTS.PATIENT_PACKAGES(id), clinicId ? { clinic_id: clinicId } : params],
        [API.ENDPOINTS.DOCTOR_BILLINGS(params.doctor_id), {
          patient_id: id, patient_phone: patient.patient_phone || '', patient_file_number: patient.file_number || patient.file_id || '',
          clinic_id: clinicId || patient.clinic_id || '', use_clinic_scope: clinicId || patient.clinic_id ? 'true' : 'false', limit: 500,
        }],
        [API.ENDPOINTS.PATIENT_EXTERNAL_REQUESTS(id), { doctorId: params.doctor_id }],
        ['/api/patients/reports', {
          patient_id: id, patient_phone: patient.patient_phone, doctor_id: patient.doctor_id,
          mongo_id: patient._id, ...(patient.file_number || patient.file_id ? { file_number: String(patient.file_number || patient.file_id).trim() } : {}),
          ...(clinicScopeIds.length ? { use_clinic_scope: 'true', clinic_ids: clinicScopeIds.join(',') } : {}),
        }],
      ];
      if (process.env.REACT_APP_ASSESSMENT_FORMS_ENABLED === 'true') requests.push(
        [API.ENDPOINTS.ASSESSMENT_FORM_TEMPLATES, { doctor_id: doctorId, clinic_id: clinicId }],
        [API.ENDPOINTS.ASSESSMENT_FORM_RESPONSES, { doctor_id: doctorId, clinic_id: clinicId, patient_id: patient._id }],
      );
      for (const [path, query] of requests) {
        try {
          const headers = path.startsWith('/api/assessment-forms') ? { Authorization: `Bearer ${await auth.currentUser?.getIdToken()}` } : {};
          const result = await profileGet(`${API.BASE_URL}${path}`, { params: query, headers });
          if ('offline' in result && result.offline) throw new Error('Connection unavailable');
          if (path === '/api/patients/reports') {
            for (const report of result.data?.data || []) {
              if (report.image_url) try { await downloadMedia(report.image_url); } catch { failed++; }
            }
          }
        } catch { failed++; }
      }
      for (const note of patient.voice_notes || []) {
        if (note.audioUrl) try { await downloadMedia(note.audioUrl); } catch { failed++; }
      }
      count++;
      progress(`${count} patient files downloaded${failed ? `; ${failed} sections unavailable` : ''}`);
    }
    const pagination = response.data.pagination;
    const total = Number(response.data.totalItems);
    if (pagination ? !pagination.hasNextPage : Number.isFinite(total) ? page * 100 >= total : patients.length < 100) break;
    page++;
  }
  return { count, failed };
}
